import { Bank } from "@/models/bank.model";
import {
  CommissionMode,
  FinancialSettingsPixKeyType,
  FinancialSettingsPixKeyTypeConfig,
  FinancialSettingsPixKeyTypeLabels,
  FinancialSettingsTax,
  PixKeyTypeFromBackend,
  PixKeyTypeToBackend,
  Tax,
} from "@/models/financialSettings.model";
import { updateCorban } from "@/services/corban.service";
import {
  createFinancialSettings,
  fetchCommissionModes,
  fetchTaxes,
  updateFinancialSettings,
} from "@/services/financialSettings.service";
import { cpfFormatter } from "@/utils/cpfFormatter";
import { removeMask } from "@/utils/removeMask";
import { CheckIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  Input,
  InputGroup,
  InputRightAddon,
  Select,
  Stack,
  Switch,
  Text,
  VStack,
  useToast,
} from "@chakra-ui/react";
import { Field, FieldArray, FieldProps, Formik, FormikHelpers } from "formik";
import { useEffect, useState } from "react";
import InputMask from "react-input-mask";
import ReactSelect from "react-select";
import TextInputField from "../TextInputField";
import AuditLogsFinancialSettingsChanges from "./AuditLogsFinancialSettingsChanges";
import { financialSettingsValidationSchema } from "./financialSettingsValidation";

interface FinancialSettingsFormProps {
  banks: Bank[];
  loadingBanks: boolean;
  isNew: boolean;
  initialData?: any;
  corbanId?: number;
  commercialManagerProp?: string | null;
  commercialManagerId?: string | null;
  hasPromoter?: boolean;
  document?: string;
  isSeller?: boolean;
  onSubmit: (data: {
    financialSettingsId?: string;
    commission: number;
  }) => void;
}

export default function FinancialSettingsForm({
  banks,
  loadingBanks,
  isNew,
  initialData,
  corbanId,
  onSubmit,
  hasPromoter,
  document,
  isSeller = false,
  commercialManagerId
}: FinancialSettingsFormProps) {
  // console.log("isnew", isNew);
  const toast = useToast();
  const [totalCommission, setTotalCommission] = useState(100);

  const [isIrrfEnabled, setIsIrrfEnabled] = useState(false);
  const [taxes, setTaxes] = useState<Tax[]>([]);
  const [commissionModes, setCommissionModes] = useState<CommissionMode[]>([]);

  // Função para determinar o tipo de PIX baseado no tamanho do documento
  const determinePixKeyType = (pixKey?: string, pixKeyType?: string) => {
    if (!pixKey || !pixKeyType) return pixKeyType;

    // Se já temos um tipo do backend, verificar se precisa ajustar para CPF
    if (pixKeyType === FinancialSettingsPixKeyType.DOCUMENT) {
      const cleanKey = pixKey.replace(/\D/g, "");
      // CPF tem 11 dígitos, CNPJ tem 14 dígitos
      if (cleanKey.length === 11 && isSeller) {
        return FinancialSettingsPixKeyType.CPF;
      }
    }

    return pixKeyType;
  };

  const initialPixKeyType = initialData?.pixKeyType
    ? PixKeyTypeFromBackend[
    initialData.pixKeyType as keyof typeof PixKeyTypeFromBackend
    ] || initialData.pixKeyType
    : undefined;


  const initialValues = {
    commission: initialData?.commission || 0,
    bank: initialData?.bank || undefined,
    commissionEmails: initialData?.commissionEmail
      ? initialData.commissionEmail
        .split(";")
        .map((email: string) => email.trim())
      : [""],
    agency: initialData?.agency || undefined,
    bankAccount: initialData?.bankAccount || undefined,
    accountDigit: initialData?.accountDigit || undefined,
    sourceIncomeTax: initialData?.sourceIncomeTax || "",
    kaminoId: initialData?.kaminoId || undefined,
    optionBluepay: initialData?.optionBluepay || false,
    pixKeyType: determinePixKeyType(initialData?.pixKey, initialPixKeyType),
    pixKey: initialData?.pixKey || undefined,
    commercialManagerId:
      commercialManagerId ?? initialData?.commercialManagerId,
    commissionModeId: initialData?.commissionModeId || "",
    taxWithholding: initialData?.taxWithholding ?? false,
    creditPromoterTaxes: initialData?.FinancialSettingsTax
      ? initialData?.FinancialSettingsTax.map(
        (t: FinancialSettingsTax) => t.Tax
      )
      : [],
  };

  useEffect(() => {
    if (initialData?.commission) {
      setTotalCommission(100 - initialData.commission);
    }


    const fetchData = async () => {
      // try {
      //   const managersList = await getCommercialManagers();
      //   setCommercialManagers(managersList);
      // } catch (err: any) {
      //   toast({
      //     title: "Erro ao carregar Gerentes Comerciais",
      //     description: err.message,
      //     status: "error",
      //     duration: 3000,
      //     isClosable: true,
      //   });
      // }

      try {
        const allTaxes = await fetchTaxes();
        setTaxes(allTaxes);
      } catch (err: any) {
        toast({
          title: "Erro ao carregar Impostos",
          description: err.message,
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }

      try {
        const allCommissionModes = await fetchCommissionModes();
        const arrayTravaCemBonus = allCommissionModes.filter(
          (mode) => mode.repassModel == "FULL_BLUE"
        );
        setCommissionModes(arrayTravaCemBonus);
      } catch (err: any) {
        toast({
          title: "Erro ao carregar Modos de Comissão",
          description: err.message,
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    };

    fetchData();
  }, []);

  const handlePartnerCommissionChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void
  ) => {
    const value = e.target.value.replace(/[^0-9]/g, "");
    if (value === "" || (parseInt(value) >= 0 && parseInt(value) <= 100)) {
      const partnerCommission = parseInt(value) || 0;
      setTotalCommission(100 - partnerCommission);
      setFieldValue("commission", partnerCommission);
    }
  };

  const handleSubmit = async (
    values: typeof initialValues,
    formikHelpers: FormikHelpers<typeof initialValues>
  ) => {
    const { setSubmitting, setValues } = formikHelpers;
    try {
      // Impede salvar dados financeiros sem gerente comercial definido
      if (
        typeof values.commercialManagerId === "undefined" ||
        values.commercialManagerId === ""
      ) {
        toast({
          title: "Gerente Comercial obrigatório",
          description:
            "Selecione o Gerente Comercial antes de salvar os dados financeiros.",
          status: "error",
          duration: 4000,
          isClosable: true,
        });
        setSubmitting(false);
        return;
      }
      // console.log("Submitting financial settings with values:", values);

      const pix =
        values.pixKeyType === FinancialSettingsPixKeyType.PHONE ||
          values.pixKeyType === FinancialSettingsPixKeyType.DOCUMENT ||
          values.pixKeyType === FinancialSettingsPixKeyType.CPF
          ? removeMask(values.pixKey)
          : values.pixKey;
      const commissionEmail = values.commissionEmails
        .map((email: string) => email.trim().toLowerCase())
        .filter(Boolean)
        .join(";");

      const financialSettingsData = {
        Corban:{id: corbanId},
        id: initialData?.id,
        optionBluepay: values.optionBluepay,
        bank: values.bank,
        kaminoId: values.kaminoId,
        sourceIncomeTax: isIrrfEnabled
          ? parseFloat(String(values.sourceIncomeTax).replace(",", "."))
          : null,
        agency: values.agency,
        commissionEmail,
        accountDigit: values.accountDigit && String(values.accountDigit),
        bankAccount: values.bankAccount && removeMask(values.bankAccount),
        commercialManagerId: values.commercialManagerId,
        commissionModeId: "76d136ae-e594-442b-9a0e-d1b2ae87ab6b", //trava para full_blue
        pixKey: pix,
        pixKeyType: values.pixKeyType
          ? PixKeyTypeToBackend[
          values.pixKeyType as FinancialSettingsPixKeyType
          ] || values.pixKeyType
          : values.pixKeyType,
        taxWithholding: values.taxWithholding,
        FinancialSettingsTax: values.creditPromoterTaxes.map((tax: Tax) => ({
          taxId: tax?.id,
        })),
      };

      const financialSettingsResult = isNew
        ? await createFinancialSettings(financialSettingsData)
        : await updateFinancialSettings(
          financialSettingsData.id,
          financialSettingsData
        );

      if (corbanId && !isSeller) {
        await updateCorban(corbanId, {
          commission: values.commission,
          paymentSplit: hasPromoter,
          financialSettingsId: financialSettingsResult.id,
        });
      }

      onSubmit({
        financialSettingsId: isNew
          ? financialSettingsResult.id
          : initialData?.id,
        commission: values.commission,
      });

      // Mantém os dados no estado do formulário após salvar
      setValues(
        {
          ...values,
          pixKey: values.pixKey, // Mantém o valor da chave PIX
          pixKeyType: values.pixKeyType, // Mantém o tipo de chave PIX
          ...(isNew && { id: financialSettingsResult.id }),
        },
        false
      );

      toast({
        title: `Dados financeiros ${isNew ? "salvos" : "atualizados"} com sucesso.`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error: any) {
      toast({
        title: "Erro ao salvar dados financeiros",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Flex width="100%" flexDirection="column">
      <Heading fontFamily="Lexend Deca" fontSize="2xl" as="b" mb={2}>
        Dados Financeiros
      </Heading>
      <Text fontSize="sm" align="left" width="100%" color="gray.500" mb={6}>
        Configure suas informações de repasse e chave PIX
      </Text>

      <Box width="100%">
        <Formik
          initialValues={initialValues}
          validationSchema={financialSettingsValidationSchema(document, isSeller)}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({
            isSubmitting,
            values,
            handleSubmit,
            setFieldValue,
            setFieldError,
            errors,
            touched,
          }) => {
            const isFullBlueMode = (() => {
              const fullBlueMode = commissionModes.find(
                (commissionMode) => commissionMode.repassModel === "FULL_BLUE"
              );
              return values.commissionModeId === fullBlueMode?.id;
            })();

            useEffect(() => {
              if (document) {
                // Para sellers, não há restrições ou preenchimento automático para DOCUMENT
                if (isSeller && values.pixKeyType === FinancialSettingsPixKeyType.DOCUMENT) {
                  return;
                }
              } else if (
                values.pixKeyType === FinancialSettingsPixKeyType.DOCUMENT &&
                !document
              ) {
                setFieldError(
                  "pixKey",
                  "CNPJ do cadastro não foi informado. Não é possível usar CNPJ como chave PIX."
                );
              }
            }, [values.pixKeyType, document, setFieldValue, setFieldError]);

            useEffect(() => {
              if (values.sourceIncomeTax && !isIrrfEnabled) {
                setIsIrrfEnabled(true);
              }
            }, [values.sourceIncomeTax]);

            useEffect(() => {
              if (isFullBlueMode) {
                setFieldValue("taxWithholding", false);
                setFieldValue("creditPromoterTaxes", []);
              }
            }, [isFullBlueMode, setFieldValue]);

            return (
              <VStack spacing={8} w="100%">
                {hasPromoter && (
                  <Flex
                    gap="2rem"
                    width="100%"
                    alignItems="flex-start"
                    flexDirection={{ base: "column", md: "row" }}
                  >
                    <Flex direction="column">
                      <Heading
                        fontFamily="Lexend Deca"
                        fontSize="2xl"
                        as="b"
                        mb={2}
                      >
                        Comissão Total: (Promotora)
                      </Heading>
                      <InputGroup size="md">
                        <Input
                          pl="1rem"
                          color="black"
                          w="12.5rem"
                          bg="gray.200"
                          type="text"
                          value={`${totalCommission.toFixed(2)}%`}
                          isReadOnly
                          fontFamily="Lexend Deca"
                        />
                      </InputGroup>
                    </Flex>
                    <Flex direction="column">
                      <Heading
                        fontFamily="Lexend Deca"
                        fontSize="2xl"
                        as="b"
                        mb={2}
                      >
                        Comissão Corban Parceiro:
                      </Heading>
                      <InputGroup size="md">
                        <Field name="commission">
                          {({ field }: any) => (
                            <Input
                              {...field}
                              pl="1rem"
                              w="13.125rem"
                              color="black"
                              type="text"
                              onChange={(e) =>
                                handlePartnerCommissionChange(e, setFieldValue)
                              }
                              fontFamily="Lexend Deca"
                            />
                          )}
                        </Field>
                        <InputRightAddon
                          children="%"
                          fontFamily="Lexend Deca"
                          px="1rem"
                        />
                      </InputGroup>
                    </Flex>
                  </Flex>
                )}
                {values.commission > 100 && (
                  <Text
                    color="red.500"
                    fontSize="sm"
                    mt={1}
                    fontFamily="Lexend Deca"
                  >
                    A comissão não pode ser maior que 100%
                  </Text>
                )}
                {/* <Flex
                  gap="2rem"
                  width="100%"
                  alignItems="flex-start"
                  flexDirection={{ base: "column", md: "row" }}
                >
                  <Field name="commercialManagerId">
                    {({ field, form }: FieldProps) => {
                      const selectValue = field.value === null ? "null" : (field.value ?? "");

                      return (
                        <FormControl
                          isInvalid={
                            !!(
                              form.errors.commercialManagerId &&
                              form.touched.commercialManagerId
                            )
                          }
                        >
                          <FormLabel>Gerente Comercial</FormLabel>
                          <Select
                            {...field}
                            value={selectValue}
                            isDisabled={!commercialManagerProp}
                            onChange={(e) => {
                              const val = e.target.value;
                              if (val === "null") {
                                form.setFieldValue("commercialManagerId", null);
                              } else {
                                form.setFieldValue("commercialManagerId", val);
                              }
                            }}
                          >
                            <option value="" disabled hidden>
                              Selecione o Gerente Comercial
                            </option>
                            <option value="null">GranaTech</option>
                            {commercialManagers.map((manager) => (
                              <option key={manager.id} value={manager.id}>
                                {manager.name} {manager.lastName}
                              </option>
                            ))}
                          </Select>
                        </FormControl>
                      );
                    }}
                  </Field>
                </Flex> */}

                <FieldArray name="commissionEmails">
                  {({ push, remove }) => (
                    <Flex flexDirection="column" gap="1rem" width="100%">
                      {values.commissionEmails.map(
                        (_email: string, index: number) => (
                          <Flex key={index} gap="1rem" width="100%">
                            <Field name={`commissionEmails.${index}`}>
                              {({ field, form }: any) => (
                                <FormControl
                                  isInvalid={
                                    !!form.errors.commissionEmails &&
                                    !!form.errors.commissionEmails[index] &&
                                    form.touched.commissionEmails &&
                                    form.touched.commissionEmails[index]
                                  }
                                >
                                  <FormLabel>Email de Comissão</FormLabel>
                                  <Input
                                    {...field}
                                    placeholder="email@exemplo.com"
                                    type="email"
                                    onBlur={(e) => {
                                      form.setFieldTouched(field.name, true);
                                      field.onBlur(e);
                                    }}
                                  />
                                  <FormErrorMessage>
                                    {form.errors.commissionEmails &&
                                      form.errors.commissionEmails[index]}
                                  </FormErrorMessage>
                                </FormControl>
                              )}
                            </Field>
                            {values.commissionEmails.length > 1 && (
                              <Button
                                onClick={() => remove(index)}
                                colorScheme="red"
                                mt="2rem"
                                height="2.5rem"
                              >
                                Remover
                              </Button>
                            )}
                          </Flex>
                        )
                      )}
                      <Button
                        onClick={() => push("")}
                        colorScheme="primary"
                        w="16rem"
                      >
                        Adicionar mais um Email
                      </Button>
                    </Flex>
                  )}
                </FieldArray>

                <Flex gap="2rem" width="100%">
                  <Field name="pixKeyType">
                    {({ field, form }: FieldProps) => (
                      <FormControl
                        isInvalid={
                          !!(form.errors.pixKeyType && form.touched.pixKeyType)
                        }
                      >
                        <FormLabel>Tipo de Chave Pix</FormLabel>
                        <Select
                          {...field}
                          placeholder="Selecione o tipo de chave Pix"
                        >
                          {Object.values(FinancialSettingsPixKeyType)
                            .filter((keyType) => {
                              // Mostrar CPF apenas para sellers
                              if (keyType === FinancialSettingsPixKeyType.CPF) {
                                return isSeller === true;
                              }
                              return true;
                            })
                            .map((keyType) => (
                              <option key={keyType} value={keyType}>
                                {
                                  FinancialSettingsPixKeyTypeLabels[
                                  keyType as FinancialSettingsPixKeyType
                                  ]
                                }
                              </option>
                            ))}
                        </Select>
                        <FormErrorMessage>
                          {typeof form.errors.pixKeyType === "string"
                            ? form.errors.pixKeyType
                            : ""}
                        </FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>
                  {values.pixKeyType &&
                    values.pixKeyType !== "BANK_ACCOUNT" && (
                      <Field name="pixKey">
                        {({ field, form }: FieldProps) => {
                          const pixKeyType =
                            values.pixKeyType as FinancialSettingsPixKeyType;
                          const config =
                            FinancialSettingsPixKeyTypeConfig[pixKeyType];

                          return (
                            <FormControl
                              isInvalid={
                                !!(form.errors.pixKey && form.touched.pixKey)
                              }
                            >
                              <FormLabel>
                                {FinancialSettingsPixKeyTypeLabels[pixKeyType]}
                              </FormLabel>
                              {config.mask ? (
                                <Input
                                  as={InputMask as any}
                                  mask={config.mask}
                                  maskChar=""
                                  value={field.value || ""}
                                  onChange={(e: any) => {
                                    form.setFieldValue(
                                      "pixKey",
                                      e.target.value
                                    );
                                  }}
                                  placeholder={config.placeholder}
                                // maxLength={config.maxLength}
                                />
                              ) : (
                                <Input
                                  {...field}
                                  placeholder={config.placeholder}
                                  // maxLength={config.maxLength}
                                  onChange={(e) => {
                                    let value = e.target.value;
                                    if (pixKeyType === FinancialSettingsPixKeyType.EMAIL) {
                                      value = value.toLowerCase();
                                    } else if (pixKeyType === FinancialSettingsPixKeyType.CPF) {
                                      value = cpfFormatter(value);
                                    }
                                    form.setFieldValue("pixKey", value);
                                  }}
                                />
                              )}
                              <FormErrorMessage>
                                {typeof form.errors.pixKey === "string"
                                  ? form.errors.pixKey
                                  : ""}
                              </FormErrorMessage>
                            </FormControl>
                          );
                        }}
                      </Field>
                    )}
                </Flex>

                {values.pixKeyType === "BANK_ACCOUNT" && (
                  <Flex gap="2rem" width="100%">
                    <Field name="bank">
                      {({ field, form }: FieldProps) => {
                        const error = form.errors[field.name];
                        const touched = form.touched[field.name];

                        return (
                          <FormControl isInvalid={!!error && !!touched}>
                            <FormLabel>Banco</FormLabel>
                            <ReactSelect
                              placeholder="Selecione o Banco"
                              isDisabled={loadingBanks}
                              onChange={(selectedOption: any) => {
                                form.setFieldValue(
                                  field.name,
                                  selectedOption?.value || ""
                                );
                              }}
                              value={
                                banks.find((bank) => bank.id === field.value)
                                  ? {
                                    value: field.value,
                                    label: `${banks.find((bank) => bank.id === field.value)?.name} - ${field.value}`,
                                  }
                                  : undefined
                              }
                              options={banks.map((bank) => ({
                                value: bank.id,
                                label: `${bank.name} - ${bank.id}`,
                              }))}
                              styles={{
                                control: (provided: any) => ({
                                  ...provided,
                                  fontFamily: "'Lexend Deca', sans-serif",
                                }),
                                option: (provided: any, state: any) => ({
                                  ...provided,
                                  backgroundColor: state.isSelected
                                    ? "#2684FF"
                                    : state.isFocused
                                      ? "#E6F0FF"
                                      : "white",
                                  color: state.isSelected ? "white" : "black",
                                  fontFamily: "'Lexend Deca', sans-serif",
                                }),
                              }}
                            />
                            {touched && typeof error === "string" && (
                              <FormErrorMessage>{error}</FormErrorMessage>
                            )}
                          </FormControl>
                        );
                      }}
                    </Field>
                    <TextInputField
                      name="agency"
                      label="Agência Bancária"
                      placeholder="0000"
                      value={values.agency}
                      error={errors.agency as string}
                      touched={touched.agency as boolean}
                      setFieldValue={setFieldValue}
                      maxLength={5}
                    />
                    <TextInputField
                      name="bankAccount"
                      label="Conta Bancária"
                      placeholder="00000000"
                      value={values.bankAccount}
                      error={errors.bankAccount as string}
                      touched={touched.bankAccount as boolean}
                      setFieldValue={setFieldValue}
                      maxLength={20}
                    />
                    <TextInputField
                      name="accountDigit"
                      label="Dígito"
                      placeholder="0"
                      value={values.accountDigit}
                      error={errors.accountDigit as string}
                      touched={touched.accountDigit as boolean}
                      setFieldValue={setFieldValue}
                      maxLength={1}
                    />
                  </Flex>
                )}

                {/* <Flex gap="2rem" width="100%">
                  <Field name="kaminoId">
                    {({ field, form }: any) => (
                      <FormControl
                        isInvalid={
                          !!form.errors.kaminoId && form.touched.kaminoId
                        }
                      >
                        <FormLabel>Id Kamino</FormLabel>
                        <Input
                          {...field}
                          placeholder="kamino-00000"
                          type="text"
                          onBlur={(e) => {
                            form.setFieldTouched(field.name, true);
                            field.onBlur(e);
                          }}
                        />
                        <FormErrorMessage>
                          {form.errors.kaminoId}
                        </FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>
                </Flex> */}

                <Field name="commissionModeId">
                  {({  form }: FieldProps) => (
                    <FormControl
                      isInvalid={
                        !!(
                          form.errors.commissionModeId &&
                          form.touched.commissionModeId
                        )
                      }
                    >
                      <FormLabel>Modelo de Repasse Comissão</FormLabel>
                      <Select
                      disabled
                        // {...field}
                        // placeholder="Selecione o modelo de repasse de comissão"
                      >
                        {commissionModes.map((commissionMode) => (
                          <option
                            key={commissionMode.id}
                            value={commissionMode.id}
                          >
                            {commissionMode.description}
                          </option>
                        ))}
                      </Select>
                      {/* <FormErrorMessage>
                        {typeof form.errors.commissionModeId === "string"
                          ? form.errors.commissionModeId
                          : ""}
                      </FormErrorMessage> */}
                    </FormControl>
                  )}
                </Field>

                {/* <Flex gap="2rem" width="100%" align="center">
                  <FormLabel htmlFor="taxWithholding">
                    Retenção de impostos na fonte ?
                  </FormLabel>
                  <Switch
                    id="taxWithholding"
                    isChecked={values.taxWithholding}
                    isDisabled={isFullBlueMode}
                    onChange={(e) => {
                      if (!isFullBlueMode) {
                        setFieldValue("taxWithholding", e.target.checked);
                        if (!e.target.checked) {
                          setFieldValue("creditPromoterTaxes", []);
                        }
                      }
                    }}
                  />
                  {isFullBlueMode && (
                    <Text fontSize="sm" color="gray.500" ml={2}>
                      (Impostos a cargo do parceiro BluePay)
                    </Text>
                  )}
                </Flex> */}
                {values.taxWithholding && !isFullBlueMode && (
                  <Stack spacing={2} width="100%">
                    {taxes.map((tax) => {
                      const isSelected = values.creditPromoterTaxes.some(
                        (cpt: Tax) => cpt!.id === tax.id
                      );
                      return (
                        <Flex
                          key={tax.id}
                          gap="2rem"
                          width="100%"
                          align="center"
                        >
                          <FormLabel htmlFor={tax.name}>
                            {tax.name} (
                            {(tax.percentage * 100)
                              .toFixed(2)
                              .replace(".", ",")}
                            %)
                          </FormLabel>
                          <Switch
                            id={tax.name}
                            isChecked={isSelected}
                            onChange={() =>
                              setFieldValue(
                                "creditPromoterTaxes",
                                isSelected
                                  ? values.creditPromoterTaxes.filter(
                                    (cpt: Tax) => cpt!.id !== tax.id
                                  )
                                  : [...values.creditPromoterTaxes, tax]
                              )
                            }
                          />
                          {isSelected && <CheckIcon color="green.500" />}
                        </Flex>
                      );
                    })}
                  </Stack>
                )}

                <Flex justifyContent="center">
                  <Button
                    onClick={() => handleSubmit()}
                    colorScheme="secondary"
                    width="20rem"
                    isLoading={isSubmitting}
                    loadingText="Salvando"
                  >
                    {isNew
                      ? "Adicionar dados financeiros"
                      : "Salvar Alterações"}
                  </Button>
                </Flex>
              </VStack>
            );
          }}
        </Formik>
      </Box>
      <Box width="100%">
        {initialData?.id && (
          <AuditLogsFinancialSettingsChanges
            commissionModes={commissionModes}
            financialSettingsId={initialData?.id}
          />
        )}
      </Box>
    </Flex>
  );
}
